import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../apiUrl';

const formatPrice = (price) => {
    return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(price || 0);
};

const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('id-ID', {
        day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
};

const OrderDetailPage = () => {
    const { orderId } = useParams();
    const navigate = useNavigate();
    const [order, setOrder] = useState(null);
    const [category, setCategory] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchDetail = async () => {
            try {
                // Fetch Order
                const orderRes = await axios.get(`${API_URL}/order/track/${orderId}`);
                const orderData = orderRes.data;
                setOrder(orderData);

                // Fetch Category for Icon
                const catRes = await axios.get(`${API_URL}/category`);
                const categories = catRes.data;
                const match = categories.find(c => c.description === orderData.game);
                setCategory(match);

            } catch (err) {
                console.error("Fetch detail error:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchDetail();
    }, [orderId]);

    if (loading) {
        return (
            <div className="container py-5 text-center">
                <div className="spinner-border text-teal" role="status"></div>
                <p className="mt-3">Memuat detail pesanan...</p>
            </div>
        );
    }

    if (!order) {
        return (
            <div className="container py-5 text-center">
                <i className="bi bi-exclamation-circle fs-1 text-danger"></i>
                <h3 className="mt-3">Pesanan Tidak Ditemukan</h3>
                <button className="buy-btn mt-3" onClick={() => navigate('/profile')}>Kembali</button>
            </div>
        );
    }

    const total = (order.price || 0) + (order.adminFee || 0) + (order.uniqueCode || 0);

    return (
        <div id="orderdetailpage" className="py-4">
            <div className="container">
                <button className="btn btn-link text-decoration-none mb-4 p-0 text-teal" onClick={() => navigate('/profile')}>
                    <i className="bi bi-arrow-left me-2"></i>Kembali ke Pesanan Saya
                </button>

                <div className="row g-4">
                    {/* Detail Pesanan */}
                    <div className="col-lg-4">
                        <div className="order-detail-card h-100">
                            <h6 className="card-label-title">Detail Pesanan</h6>
                            <div className="order-info-grid">
                                <div className="info-item">
                                    <span>Order ID</span>
                                    <span className="fw-bold">{order.id} <i className="bi bi-copy ms-1 small text-muted"></i></span>
                                </div>
                                <div className="info-item">
                                    <span>Catatan</span>
                                    <span className="text-teal fw-bold">{order.notes || 'Pesanan dalam proses'}</span>
                                </div>
                                <div className="info-item">
                                    <span>Tanggal Transaksi</span>
                                    <span>{formatDate(order.date || order.createdAt)}</span>
                                </div>
                                <hr />
                                <div className="info-item">
                                    <span>Harga</span>
                                    <span>{formatPrice(order.price)}</span>
                                </div>
                                <div className="info-item">
                                    <span>Biaya Admin</span>
                                    <span>{formatPrice(order.adminFee)}</span>
                                </div>
                                <div className="info-item">
                                    <span>Kode Unik</span>
                                    <span>{formatPrice(order.uniqueCode)}</span>
                                </div>
                                <hr />
                                <div className="info-item total-row">
                                    <span className="fw-bold">Total</span>
                                    <span className="text-teal fw-bold fs-5">{formatPrice(total)}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Detail Produk */}
                    <div className="col-lg-4">
                        <div className="order-detail-card h-100">
                            <h6 className="card-label-title">Detail Produk</h6>
                            <div className="d-flex align-items-center gap-3 mt-3">
                                <div className="product-icon-wrapper">
                                    <img src={category?.image || '/logo.png'} alt={order.game} className="img-fluid rounded" />
                                </div>
                                <div>
                                    <div className="fw-bold">{order.game}</div>
                                    <div className="small text-muted">{order.productName || order.item}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Detail Player */}
                    <div className="col-lg-4">
                        <div className="order-detail-card h-100">
                            <h6 className="card-label-title">Detail Player</h6>
                            <div className="order-info-grid mt-3">
                                <div className="info-item">
                                    <span>ID</span>
                                    <span className="fw-bold">{order.userId}</span>
                                </div>
                                {order.zoneId && (
                                    <div className="info-item">
                                        <span>Region/Zone</span>
                                        <span className="fw-bold">{order.zoneId}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Status Banner */}
                    <div className="col-12">
                        <div className="status-banner-card text-center py-5">
                            <div className="status-icon-glow mb-4">
                                {order.status === 'Berhasil' ? (
                                    <i className="bi bi-check-circle-fill text-teal display-1"></i>
                                ) : order.status === 'Gagal' || order.status === 'Batal' ? (
                                    <i className="bi bi-x-circle-fill text-danger display-1"></i>
                                ) : (
                                    <i className="bi bi-stopwatch text-teal display-1 animate-pulse"></i>
                                )}
                            </div>
                            <h3 className="fw-bold">Pesanan {order.status === 'Berhasil' ? 'Selesai' : order.status === 'Pending' ? 'Menunggu Pembayaran' : 'Sedang Diproses'}</h3>
                            <p className="text-muted">
                                {order.status === 'Berhasil' ? 'Terima kasih telah berbelanja di Family TopUp!' : 'Silahkan tunggu hingga status pesanan terupdate'}
                            </p>
                            {order.status === 'Pending' && order.paymentUrl && (
                                <a
                                    href={order.paymentUrl}
                                    className="buy-btn mt-3 d-inline-block text-decoration-none"
                                    style={{ padding: '12px 40px', fontSize: '1.1rem' }}
                                >
                                    <i className="bi bi-credit-card me-2"></i>Bayar Sekarang
                                </a>
                            )}
                        </div>
                    </div>

                    {/* Give Review Section (Visible only if order successful) */}
                    {(order.status === 'Berhasil' || order.status === 'Success') && (
                        <div className="col-12">
                            <GiveReview order={order} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const GiveReview = ({ order }) => {
    const [rating, setRating] = useState(5);
    const [hoverRating, setHoverRating] = useState(0);
    const [comment, setComment] = useState('');
    const [isSubmitted, setIsSubmitted] = useState(order.isReviewed || false);
    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState({ type: '', text: '' });

    // Mask Order ID: FC2026... -> #FC2****123
    const maskId = (id) => {
        const str = String(id);
        if (str.length < 6) return `#${str}`;
        return `#${str.substring(0, 3)}****${str.substring(str.length - 3)}`;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMsg({ type: '', text: '' });

        try {
            await axios.post(`${API_URL}/api/reviews`, {
                orderId: order.id,
                game: order.game,
                productName: order.productName || order.item,
                rating,
                comment,
                userDisplay: maskId(order.id)
            });
            setIsSubmitted(true);
        } catch (err) {
            setMsg({ type: 'danger', text: err.response?.data?.error || 'Gagal mengirim penilaian' });
        } finally {
            setLoading(false);
        }
    };

    if (isSubmitted) {
        return (
            <div className="give-review-box text-center py-5">
                <i className="bi bi-heart-fill text-danger fs-1 mb-3 animate-pulse"></i>
                <h4 className="fw-bold">Terima Kasih!</h4>
                <p className="text-muted">Penilaian Anda sangat berarti bagi kami dan pelanggan lainnya.</p>
            </div>
        );
    }

    return (
        <div className="give-review-box">
            <h5 className="fw-bold mb-3">Beri Penilaian</h5>
            <div className="mb-4">
                <p className="text-muted small mb-2">Seberapa puas Anda dengan layanan kami?</p>
                <div className="star-rating-input">
                    {[1, 2, 3, 4, 5].map(num => (
                        <button
                            key={num}
                            className={`star-btn ${ (hoverRating || rating) >= num ? 'active' : ''}`}
                            onMouseEnter={() => setHoverRating(num)}
                            onMouseLeave={() => setHoverRating(0)}
                            onClick={() => setRating(num)}
                        >
                            <i className={`bi ${ (hoverRating || rating) >= num ? 'bi-star-fill' : 'bi-star'}`}></i>
                        </button>
                    ))}
                </div>
            </div>

            <form onSubmit={handleSubmit}>
                <div className="mb-3">
                    <label className="form-label text-muted small">Tulis pesan (Opsional)</label>
                    <textarea 
                        className="form-control review-textarea" 
                        rows="3" 
                        placeholder="Masukan pesan pengalaman belanja Anda..."
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                    ></textarea>
                </div>
                
                {msg.text && <div className={`alert alert-${msg.type} tiny-alert`}>{msg.text}</div>}

                <button type="submit" className="btn-submit-review" disabled={loading}>
                    {loading ? 'Mengirim...' : 'Kirim Penilaian'}
                </button>
            </form>
        </div>
    );
};

export default OrderDetailPage;
